// The editing interface drawn over the content, inside the canvas: hover and selection frames,
// the block toolbar, the drop marker and the column resize handles. Nothing here is ever exported.
import { hFor, clamp } from './dom.js';
import { uiIcons } from '../ui/icons.js';
import { blockOf } from './registry.js';
import { BREAKPOINTS, columnSpan, setColumnSpanFrom, editingBreakpoint } from './grid.js';

export class Overlay {
    constructor(editor) {
        this.editor = editor;
        const { doc, ui } = editor.canvas;
        this.doc = doc;
        const h = hFor(doc);
        this.h = h;

        this.hoverBox = h('div', { class: 'pf-ui-box pf-ui-hover' }, h('span', { class: 'pf-ui-label' }));
        this.selectBox = h('div', { class: 'pf-ui-box pf-ui-select' }, h('span', { class: 'pf-ui-label' }));
        this.drop = h('div', { class: 'pf-ui-drop' });
        this.toolbar = h('div', { class: 'pf-ui-toolbar', role: 'toolbar' });
        this.handles = h('div', { class: 'pf-ui-handles' });
        this.empty = h('div', { class: 'pf-ui-empty' });
        ui.append(this.hoverBox, this.selectBox, this.handles, this.drop, this.toolbar, this.empty);

        this.buildToolbar();
        this.buildEmptyState();
        this.hovered = null;
        this.frame = 0;
        this.refresh = this.refresh.bind(this);
    }

    button(name, title, action, extra = {}) {
        const btn = this.h('button', { type: 'button', class: 'pf-ui-btn', title, 'aria-label': title, dataset: { action: name }, ...extra });
        btn.append(this.svg(uiIcons[extra.iconName || name] || ''));
        btn.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            action(event);
        });
        return btn;
    }

    svg(markup) {
        const span = this.doc.createElement('span');
        span.className = 'pf-ui-icon';
        span.innerHTML = `<svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">${markup}</svg>`;
        return span;
    }

    buildToolbar() {
        const { t } = this.editor;
        const e = this.editor;
        this.parentBtn = this.button('arrow-elbow-left-up', t('action.selectParent'), () => e.selectParent());
        this.moveBtn = this.button('arrows-out-cardinal', t('action.move'), () => {}, { class: 'pf-ui-btn pf-ui-move' });
        // The move handle starts a drag on pointerdown; a click alone does nothing.
        this.moveBtn.addEventListener('pointerdown', event => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            if (e.selected) e.dnd.startMove(e.selected, event, this.moveBtn);
        });
        this.toolbar.append(
            this.parentBtn,
            this.moveBtn,
            this.button('arrow-up', t('action.moveUp'), () => e.moveSelected(-1)),
            this.button('arrow-down', t('action.moveDown'), () => e.moveSelected(1)),
            this.button('copy', t('action.duplicate'), () => e.duplicateSelected()),
            this.button('trash', t('action.remove'), () => e.removeSelected(), { class: 'pf-ui-btn pf-ui-danger' }),
        );
    }

    buildEmptyState() {
        const { t } = this.editor;
        const h = this.h;
        this.empty.append(
            h('div', { class: 'pf-ui-empty-title' }, t('empty.title')),
            h('div', { class: 'pf-ui-empty-text' }, t('empty.text')),
        );
        const actions = h('div', { class: 'pf-ui-empty-actions' });
        if (this.editor.options.sections !== false) {
            actions.append(h('button', { type: 'button', class: 'pf-ui-empty-btn', onclick: () => this.editor.app && this.editor.app.showTab('sections') }, t('empty.sections')));
        }
        actions.append(h('button', { type: 'button', class: 'pf-ui-empty-btn pf-ui-empty-btn-alt', onclick: () => this.editor.app && this.editor.app.showTab('blocks') }, t('empty.blocks')));
        this.empty.append(actions);
    }

    labelOf(el) {
        const def = blockOf(el);
        if (!def) return '';
        if (def.name === 'heading' || def.name === 'box') {
            const inner = def.name === 'box' ? el.localName : el.localName.toUpperCase();
            return `${this.editor.t(def.label)} ${def.name === 'box' ? `<${inner}>` : inner}`;
        }
        if (def.name === 'col') {
            const bp = this.editor.breakpoint();
            const span = columnSpan(el, bp);
            return `${this.editor.t(def.label)}${span ? ` ${span}/12` : ''}`;
        }
        return this.editor.t(def.label);
    }

    place(box, el) {
        if (!el || !el.isConnected) {
            box.style.display = 'none';
            return null;
        }
        const rect = el.getBoundingClientRect();
        box.style.display = 'block';
        box.style.transform = `translate(${Math.round(rect.left)}px, ${Math.round(rect.top)}px)`;
        box.style.width = `${Math.max(0, Math.round(rect.width))}px`;
        box.style.height = `${Math.max(0, Math.round(rect.height))}px`;
        box.dataset.kind = (blockOf(el) || {}).kind || '';
        box.dataset.block = (blockOf(el) || {}).name || '';
        return rect;
    }

    setHover(el) {
        if (this.hovered === el) return;
        this.hovered = el;
        this.schedule();
    }

    schedule() {
        if (this.frame) return;
        this.frame = this.doc.defaultView.requestAnimationFrame(this.refresh);
    }

    refresh() {
        this.frame = 0;
        const e = this.editor;
        const view = this.doc.defaultView;
        const selected = e.selected && e.selected.isConnected ? e.selected : null;
        const dragging = e.dnd && e.dnd.active;
        const preview = e.isPreview();

        // Empty page: an invitation instead of a blank canvas.
        this.empty.style.display = !e.canvas.root.firstElementChild && !preview ? 'flex' : 'none';

        const hovered = !dragging && !preview && this.hovered && this.hovered !== selected ? this.hovered : null;
        const hoverRect = this.place(this.hoverBox, hovered);
        if (hoverRect) this.hoverBox.firstChild.textContent = this.labelOf(hovered);

        const rect = preview ? null : this.place(this.selectBox, selected);
        this.selectBox.classList.toggle('pf-ui-editing', !!(selected && e.text && e.text.active && selected.contains(e.text.active)));
        if (!rect) {
            this.toolbar.style.display = 'none';
            this.handles.replaceChildren();
            return;
        }
        this.selectBox.firstChild.textContent = this.labelOf(selected);
        this.parentBtn.disabled = !e.parentBlock(selected);

        // While a text is edited, its formatting bar takes the place of the block toolbar.
        const editingText = e.text && e.text.active && e.text.mode !== 'plain' && !e.text.adapter;
        this.toolbar.style.display = dragging || editingText ? 'none' : 'flex';
        if (editingText) e.text.position();
        // Toolbar: above the top right corner, inside the frame when there is no room above.
        const k = e.uiScale();
        const width = (this.toolbar.offsetWidth || 180) * k;
        const height = (this.toolbar.offsetHeight || 30) * k;
        const left = clamp(rect.right - width, 4, Math.max(4, view.innerWidth - width - 4));
        const top = rect.top - height - 4 * k >= 2 ? rect.top - height - 4 * k : clamp(rect.top + 4, 2, view.innerHeight - height - 2);
        this.toolbar.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px) scale(${k})`;

        this.placeHandles(selected, dragging);
    }

    /** Handles between the columns of the selected row (or of the row of the selected column). */
    placeHandles(selected, dragging) {
        if (this.resizing) {
            // The handle being dragged must stay the same element: the handles only follow their columns.
            this.handles.querySelectorAll('.pf-ui-handle').forEach(handle => this.placeHandle(handle, ...handle.pfColumns));
            return;
        }
        this.handles.replaceChildren();
        if (dragging) return;
        const def = blockOf(selected);
        const row = def && def.kind === 'row' ? selected : def && def.kind === 'col' ? selected.parentElement : null;
        if (!row) return;
        const cols = Array.from(row.children).filter(child => child.getAttribute('data-pf') === 'col');
        const bp = this.editor.breakpoint();
        for (let i = 0; i < cols.length - 1; i++) {
            const a = cols[i].getBoundingClientRect();
            const b = cols[i + 1].getBoundingClientRect();
            // Side by side (the next one starts on the right and they share a band), whatever their alignment.
            const sideBySide = b.left >= a.right - 8 && b.top < a.bottom && a.top < b.bottom;
            if (!sideBySide) continue; // stacked columns: nothing to resize
            if (columnSpan(cols[i], bp) === null || columnSpan(cols[i + 1], bp) === null) continue;
            const handle = this.h('div', { class: 'pf-ui-handle', title: this.editor.t('action.resizeColumns') });
            handle.pfColumns = [cols[i], cols[i + 1]];
            this.placeHandle(handle, cols[i], cols[i + 1]);
            handle.addEventListener('pointerdown', event => this.resizeColumns(event, row, cols[i], cols[i + 1], handle));
            this.handles.append(handle);
        }
    }

    /** Puts a handle on the line between its two columns, over their height. */
    placeHandle(handle, left, right) {
        const a = left.getBoundingClientRect();
        const b = right.getBoundingClientRect();
        const top = Math.min(a.top, b.top);
        handle.style.transform = `translate(${Math.round((a.right + b.left) / 2 - 5)}px, ${Math.round(top)}px)`;
        handle.style.height = `${Math.round(Math.max(a.bottom, b.bottom) - top)}px`;
    }

    resizeColumns(event, row, left, right, handle) {
        event.preventDefault();
        event.stopPropagation();
        const bp = this.editor.breakpoint();
        const editBp = editingBreakpoint(bp);
        const total = columnSpan(left, bp) + columnSpan(right, bp);
        const rowRect = row.getBoundingClientRect();
        const unit = rowRect.width / 12;
        const start = left.getBoundingClientRect().left;
        handle.setPointerCapture(event.pointerId);
        handle.classList.add('pf-active');
        this.resizing = true;
        const tip = this.h('div', { class: 'pf-ui-tip' });
        this.handles.append(tip);
        const move = moveEvent => {
            const span = clamp(Math.round((moveEvent.clientX - start) / unit), 1, total - 1);
            if (span !== columnSpan(left, bp)) {
                setColumnSpanFrom(left, editBp, span);
                setColumnSpanFrom(right, editBp, total - span);
            }
            tip.textContent = `${span} / ${total - span} · ${(BREAKPOINTS.find(b => b.key === editBp) || {}).label}+`;
            tip.style.transform = `translate(${Math.round(moveEvent.clientX + 12)}px, ${Math.round(moveEvent.clientY + 12)}px) scale(${this.editor.uiScale()})`;
            this.schedule();
        };
        const up = () => {
            this.resizing = false;
            handle.removeEventListener('pointermove', move);
            handle.removeEventListener('pointerup', up);
            handle.removeEventListener('pointercancel', up);
            this.editor.commit();
            this.editor.app && this.editor.app.inspector.render();
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', up);
    }

    /** Drop marker: a line between blocks, or the whole frame of an empty block. */
    showDrop(spot) {
        if (!spot) {
            this.drop.style.display = 'none';
            return;
        }
        const { parent, before, axis } = spot;
        const children = Array.from(parent.children).filter(child => child.hasAttribute('data-pf') && child !== spot.moving);
        const style = this.drop.style;
        style.display = 'block';
        const area = children.length === 0 || spot.area;
        this.drop.classList.toggle('pf-ui-drop-area', area);
        if (area) {
            const r = parent.getBoundingClientRect();
            style.transform = `translate(${Math.round(r.left + 4)}px, ${Math.round(r.top + 4)}px)`;
            style.width = `${Math.max(8, Math.round(r.width - 8))}px`;
            style.height = `${Math.max(8, Math.round(Math.min(r.height - 8, 400)))}px`;
            return;
        }
        const index = before ? children.indexOf(before) : children.length;
        const prev = children[index - 1] ? children[index - 1].getBoundingClientRect() : null;
        const next = children[index] ? children[index].getBoundingClientRect() : null;
        if (axis === 'x') {
            const ref = next || prev;
            const x = next && prev && Math.abs(prev.top - next.top) < 4 ? (prev.right + next.left) / 2 : next ? next.left : prev.right;
            style.transform = `translate(${Math.round(x - 2)}px, ${Math.round(ref.top)}px)`;
            style.width = '4px';
            style.height = `${Math.round(ref.height)}px`;
        } else {
            const p = parent === this.editor.canvas.root ? null : parent.getBoundingClientRect();
            const ref = next || prev;
            const y = prev && next ? (prev.bottom + next.top) / 2 : next ? next.top - 2 : prev.bottom + 2;
            const left = p ? p.left + 4 : ref.left;
            const width = p ? p.width - 8 : ref.width;
            style.transform = `translate(${Math.round(left)}px, ${Math.round(y - 2)}px)`;
            style.width = `${Math.round(width)}px`;
            style.height = '4px';
        }
    }
}
